import React, { useRef, useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import Footer from '../components/footer';
import ThemeProvider from '../components/ThemeProvider';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import logo from '../assets/img/logo.png';
import { Helmet } from 'react-helmet';
import { getInternationalMetaTags } from '../utils/seoInternational';

const MainLayout = () => {
  const location = useLocation();
  // Navigation state
  const [isOpen, setIsOpen] = useState(false);
  const [productsDropdown, setProductsDropdown] = useState(false);
  const [contactDropdown, setContactDropdown] = useState(false);
  const [resourcesDropdown, setResourcesDropdown] = useState(false);
  const [servicesMegaOpen, setServicesMegaOpen] = useState(false);
  const servicesMegaTimeout = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const productsDropdownRef = useRef(null);
  const contactDropdownRef = useRef(null);
  const resourcesDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const servicesMegaRef = useRef(null);
  const servicesButtonRef = useRef(null);

  // Navigation effects
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        mobileMenuRef.current &&
        menuButtonRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
      if (
        productsDropdown &&
        productsDropdownRef.current &&
        !productsDropdownRef.current.contains(event.target)
      ) {
        setProductsDropdown(false);
      }
      if (
        contactDropdown &&
        contactDropdownRef.current &&
        !contactDropdownRef.current.contains(event.target)
      ) {
        setContactDropdown(false);
      }
      if (
        resourcesDropdown &&
        resourcesDropdownRef.current &&
        !resourcesDropdownRef.current.contains(event.target)
      ) {
        setResourcesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, productsDropdown, contactDropdown, resourcesDropdown]);

  // Close services mega menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        servicesMegaOpen &&
        servicesMegaRef.current &&
        servicesButtonRef.current &&
        !servicesMegaRef.current.contains(event.target) &&
        !servicesButtonRef.current.contains(event.target)
      ) {
        setServicesMegaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicesMegaOpen]);

  // Handle body scroll lock and add class for styling
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('menu-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
    };
  }, [isOpen]);

  // Scroll to top on every route change (instant + delayed retry for lazy pages)
  useEffect(() => {
    const scrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollTop();
    const frame = requestAnimationFrame(scrollTop);
    const timeout = setTimeout(scrollTop, 50);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const start = window.__preloaderStart || Date.now();
    const MIN_DISPLAY = 1800;
    const elapsed = Date.now() - start;
    const remaining = Math.max(0, MIN_DISPLAY - elapsed);

    const timer = setTimeout(() => {
      const pl = document.getElementById('preloader');
      if (pl) {
        pl.classList.add('hide');
        setTimeout(() => pl.remove(), 600);
      }
    }, remaining);
    return () => clearTimeout(timer);
  }, []);

  // Nav bar: transparent over the top of the page, blurred solid bar once scrolled
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Get international SEO tags
  const internationalSEO = getInternationalMetaTags(location.pathname);

  // NavItem component
  const NavItem = ({ to, children, external = false }) => {
    if (external) {
      return (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 xl:px-3.5 py-2 rounded-full transition-colors duration-200 font-medium text-sm xl:text-[15px] whitespace-nowrap text-[#c1c9d2] hover:text-white hover:bg-white/[0.06]"
          onClick={() => setIsOpen(false)}
        >
          {children}
        </a>
      );
    }

    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          `px-3 xl:px-3.5 py-2 rounded-full transition-colors duration-200 font-medium text-sm xl:text-[15px] whitespace-nowrap ${
            isActive
              ? "text-white bg-white/[0.08]"
              : "text-[#c1c9d2] hover:text-white hover:bg-white/[0.06]"
          }`
        }
        onClick={() => setIsOpen(false)}
      >
        {children}
      </NavLink>
    );
  };

  return (
    <ThemeProvider>
      <Helmet>
        {/* International Hreflang Tags */}
        {internationalSEO.hreflangTags.map((tag) => (
          <link key={tag.lang} rel="alternate" hreflang={tag.lang} href={tag.href} />
        ))}

        {/* Geo Targeting Meta Tags */}
        {internationalSEO.geoMetaTags.map((tag, index) => (
          <meta key={`${tag.name}-${index}`} name={tag.name} content={tag.content} />
        ))}
      </Helmet>
      <div className="min-h-screen flex flex-col bg-black text-white overflow-x-hidden">
        {/* Navigation Bar */}
        <nav
          className={`fixed top-0 left-0 right-0 z-40 h-24 transition-[background-color,border-color,backdrop-filter] duration-300 border-b ${
            scrolled || isOpen
              ? "bg-black/75 backdrop-blur-xl backdrop-saturate-150 border-white/[0.08]"
              : "bg-transparent border-transparent"
          }`}
        >
          <div className="container mx-auto h-full px-4 sm:px-6">
            <div className="flex items-center h-full relative">
              {/* Logo with glow effect - Left side */}
              <NavLink to="/" className="flex items-center relative z-10 group" aria-label="Safe Edges homepage">
                <img
                  src={logo}
                  alt="Safe Edges - Blockchain Security Audit Logo"
                  width="48"
                  height="48"
                  className="h-12 w-auto relative z-10 mix-blend-screen group-hover:brightness-110"
                  loading="eager"
                  decoding="sync"
                />
              </NavLink>

              {/* Desktop Navigation - Centered */}
              <div className="hidden lg:flex items-center gap-0.5 ml-6 xl:ml-10">
                <NavItem to="/">Home</NavItem>
                <NavItem to="/about-us">About Us</NavItem>
                {/* Services mega menu (desktop) */}
                <div
                  className="relative"
                  onMouseEnter={() => {
                    if (servicesMegaTimeout.current) clearTimeout(servicesMegaTimeout.current);
                    setServicesMegaOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (servicesMegaTimeout.current) clearTimeout(servicesMegaTimeout.current);
                    servicesMegaTimeout.current = setTimeout(() => setServicesMegaOpen(false), 220);
                  }}
                >
                  <button
                    ref={servicesButtonRef}
                    onClick={() => setServicesMegaOpen((prev) => !prev)}
                    className={`px-3 xl:px-3.5 py-2 rounded-full transition-colors duration-200 font-medium text-sm xl:text-[15px] whitespace-nowrap flex items-center gap-1.5 ${servicesMegaOpen ? "text-white bg-white/[0.06]" : "text-[#c1c9d2] hover:text-white hover:bg-white/[0.06]"}`}
                    aria-expanded={servicesMegaOpen}
                  >
                    Services
                    <FaChevronDown className={`h-2.5 w-2.5 opacity-70 transition-transform duration-200 ${servicesMegaOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div
                    ref={servicesMegaRef}
                    onMouseEnter={() => {
                      if (servicesMegaTimeout.current) clearTimeout(servicesMegaTimeout.current);
                      setServicesMegaOpen(true);
                    }}
                    onMouseLeave={() => {
                      if (servicesMegaTimeout.current) clearTimeout(servicesMegaTimeout.current);
                      servicesMegaTimeout.current = setTimeout(() => setServicesMegaOpen(false), 220);
                    }}
                    className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[820px] max-w-[94vw] rounded-2xl bg-[#0a0f16]/95 backdrop-blur-xl border border-white/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.9)] transition-all duration-200 ${servicesMegaOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}
                  >
                    <div className="p-6 grid grid-cols-4 gap-6 text-sm text-[#c1c9d2]">
                      {/* Core Security */}
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.12em] text-[#7a8899] mb-3 px-2 font-semibold">Core Security</p>
                        <div className="space-y-1.5">
                          {[
                            { label: "Smart Contract Audit", to: "/services/smart-contract-audit" },
                            { label: "Blockchain Protocol Audit", to: "/services/blockchain-protocol-audit" },
                            { label: "dApp Pentest Audit", to: "/services/pen-test" },
                            { label: "Wallet Audit", to: "/services/wallet-audit" },
                          ].map((item) => (
                            <NavLink key={item.to} to={item.to}
                              className="block py-1.5 px-2 rounded-lg hover:bg-white/[0.06] hover:text-white transition-colors"
                              onClick={() => setServicesMegaOpen(false)}>
                              {item.label}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                      {/* Growth Verticals */}
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.12em] text-[#7a8899] mb-3 px-2 font-semibold">Growth Verticals</p>
                        <div className="space-y-1.5">
                          {[
                            { label: "DeFi Diligence", to: "/services/defi-diligence" },
                            { label: "DeFAI Audit", to: "/services/defai-audit" },
                            { label: "GameFi Audit", to: "/services/gamefi-audit" },
                          ].map((item) => (
                            <NavLink key={item.to} to={item.to}
                              className="block py-1.5 px-2 rounded-lg hover:bg-white/[0.06] hover:text-white transition-colors"
                              onClick={() => setServicesMegaOpen(false)}>
                              {item.label}
                            </NavLink>
                          ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/[0.08]">
                          <p className="text-[11px] uppercase tracking-[0.12em] text-[#7a8899] mb-2 px-2 font-semibold">Enterprise</p>
                          <NavLink to="/services/vciso"
                            className="block py-1.5 px-2 rounded-lg hover:bg-white/[0.06] hover:text-white transition-colors"
                            onClick={() => setServicesMegaOpen(false)}>
                            vCISO
                          </NavLink>
                          <NavLink to="/services/blockchain-forensics"
                            className="block py-1.5 px-2 rounded-lg hover:bg-white/[0.06] hover:text-white transition-colors"
                            onClick={() => setServicesMegaOpen(false)}>
                            Blockchain Forensics
                          </NavLink>
                        </div>
                      </div>
                      {/* Chains */}
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.12em] text-[#7a8899] mb-3 px-2 font-semibold">Featured Chains</p>
                        <div className="space-y-1.5">
                          {[
                            { label: "Ethereum", to: "/services/chain/ethereum-audit" },
                            { label: "Solana", to: "/services/chain/solana-audit" },
                            { label: "Base", to: "/services/chain/base-chain-audit" },
                            { label: "Arbitrum", to: "/services/chain/arbitrum-audit" },
                            { label: "Sui", to: "/services/chain/sui-audit" },
                            { label: "Starknet", to: "/services/chain/starknet-audit" },
                          ].map((item) => (
                            <NavLink key={item.to} to={item.to}
                              className="block py-1.5 px-2 rounded-lg hover:bg-white/[0.06] hover:text-white transition-colors"
                              onClick={() => setServicesMegaOpen(false)}>
                              {item.label}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                      {/* Languages + CTA */}
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.12em] text-[#7a8899] mb-3 px-2 font-semibold">Languages</p>
                        <div className="space-y-1.5">
                          {[
                            { label: "Solidity", to: "/services/lang/solidity-audit" },
                            { label: "Rust", to: "/services/lang/rust-audit" },
                            { label: "Move", to: "/services/lang/move-audit" },
                            { label: "Sway", to: "/services/lang/sway-audit" },
                            { label: "Cairo", to: "/services/lang/cairo-audit" },
                            { label: "Noir", to: "/services/lang/noir-audit" },
                          ].map((item) => (
                            <NavLink key={item.to} to={item.to}
                              className="block py-1.5 px-2 rounded-lg hover:bg-white/[0.06] hover:text-white transition-colors"
                              onClick={() => setServicesMegaOpen(false)}>
                              {item.label}
                            </NavLink>
                          ))}
                        </div>
                        <div className="mt-5">
                          <NavLink to="/services"
                            className="flex items-center justify-center w-full px-3 py-2 rounded-full border border-white/15 text-white/90 text-xs font-semibold hover:bg-white/[0.06] hover:border-white/30 transition-colors"
                            onClick={() => setServicesMegaOpen(false)}>
                            Explore All Services
                          </NavLink>
                          <a href="https://calendly.com/vivanchauhan-safeedges/30min" target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center w-full mt-2 px-3 py-2 rounded-full bg-[#00A99E] text-black text-xs font-semibold hover:bg-[#2ee6d6] transition-colors"
                            onClick={() => setServicesMegaOpen(false)}>
                            Get Free Audit Consultation
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative mr-3">
                  <NavItem to="/zk-security">ZK Security</NavItem>
                  <span className="pointer-events-none absolute -top-2 -right-3 rounded-full bg-[#0a0f16] px-1.5 py-px text-[9px] font-semibold leading-4 text-teal-300 ring-1 ring-teal-400/40">
                    ZK
                  </span>
                </div>
                <NavItem to="/reports">Audits</NavItem>
                <div className="relative mr-3">
                  <NavItem to="/services/ai-mcp-audit">AI & MCP Audit</NavItem>
                  <span className="pointer-events-none absolute -top-2 -right-3 rounded-full bg-[#0a0f16] px-1.5 py-px text-[9px] font-semibold leading-4 text-rose-300 ring-1 ring-rose-400/40">
                    NEW
                  </span>
                </div>
                <div className="relative" ref={resourcesDropdownRef}>
                  <button
                    onClick={() => {
                      setResourcesDropdown(!resourcesDropdown);
                      setProductsDropdown(false);
                      setContactDropdown(false);
                    }}
                    className={`px-3 xl:px-3.5 py-2 rounded-full transition-colors duration-200 font-medium text-sm xl:text-[15px] whitespace-nowrap flex items-center gap-1.5 focus:outline-none ${resourcesDropdown ? "text-white bg-white/[0.06]" : "text-[#c1c9d2] hover:text-white hover:bg-white/[0.06]"}`}
                    aria-expanded={resourcesDropdown}
                  >
                    Resources
                    <FaChevronDown className={`h-2.5 w-2.5 opacity-70 transition-transform duration-200 ${resourcesDropdown ? "rotate-180" : ""}`} />
                  </button>
                  <div
                    className={`absolute right-0 top-full mt-3 w-48 p-1.5 rounded-xl bg-[#0a0f16]/95 backdrop-blur-xl border border-white/10 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.85)] transition-all duration-200 ${
                      resourcesDropdown
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <a
                      href="https://blog.safeedges.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setResourcesDropdown(false)}
                    >
                      Blog
                    </a>
                    <a
                      href="https://blog.safeedges.in/category/web3-security-case-studies-safe-edges/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setResourcesDropdown(false)}
                    >
                      Case Studies
                    </a>
                  </div>
                </div>

                {/* Products dropdown */}
                <div className="relative" ref={productsDropdownRef}>
                  <button
                    onClick={() => {
                      setProductsDropdown(!productsDropdown);
                      setResourcesDropdown(false);
                      setContactDropdown(false);
                    }}
                    className={`px-3 xl:px-3.5 py-2 rounded-full transition-colors duration-200 font-medium text-sm xl:text-[15px] whitespace-nowrap flex items-center gap-1.5 focus:outline-none ${productsDropdown ? "text-white bg-white/[0.06]" : "text-[#c1c9d2] hover:text-white hover:bg-white/[0.06]"}`}
                    aria-expanded={productsDropdown}
                  >
                    Products
                    <FaChevronDown className={`h-2.5 w-2.5 opacity-70 transition-transform duration-200 ${productsDropdown ? "rotate-180" : ""}`} />
                  </button>
                  {/* Dropdown Menu */}
                  <div
                    className={`absolute right-0 top-full mt-3 w-56 p-1.5 rounded-xl bg-[#0a0f16]/95 backdrop-blur-xl border border-white/10 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.85)] transition-all duration-200 ${
                      productsDropdown
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <NavLink
                      to="https://open-vsx.org/extension/safeedges/bastion-security"
                      className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setProductsDropdown(false)}
                    >
                      <span className="font-medium">Bastion Security</span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-black bg-[#00A99E] px-1.5 py-0.5 rounded">New</span>
                    </NavLink>
                    <a
                      href="https://crates.io/crates/swayscan/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setProductsDropdown(false)}
                    >
                      SwayScan
                    </a>
                    <a
                      href="https://evmsleuth.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setProductsDropdown(false)}
                    >
                      EVMSleuth
                    </a>
                  </div>
                </div>
                {/* Contact dropdown */}
                <div className="relative ml-3" ref={contactDropdownRef}>
                  <button
                    onClick={() => {
                      setContactDropdown(!contactDropdown);
                      setResourcesDropdown(false);
                      setProductsDropdown(false);
                    }}
                    className="flex items-center gap-1.5 rounded-full bg-[#00A99E] px-4 py-2 text-sm font-semibold text-black whitespace-nowrap transition-colors duration-200 hover:bg-[#2ee6d6] focus:outline-none"
                    aria-expanded={contactDropdown}
                  >
                    Contact
                    <FaChevronDown className={`h-2.5 w-2.5 opacity-70 transition-transform duration-200 ${contactDropdown ? "rotate-180" : ""}`} />
                  </button>
                  {/* Dropdown Menu */}
                  <div
                    className={`absolute right-0 top-full mt-3 w-48 p-1.5 rounded-xl bg-[#0a0f16]/95 backdrop-blur-xl border border-white/10 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.85)] transition-all duration-200 ${
                      contactDropdown
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <a
                      href="https://calendly.com/vivanchauhan-safeedges/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setContactDropdown(false)}
                    >
                      Schedule Meeting
                    </a>
                    <NavLink
                      to="/contact"
                      className="block rounded-lg px-3 py-2.5 text-sm text-[#c1c9d2] hover:text-white hover:bg-white/[0.06] transition-colors"
                      onClick={() => setContactDropdown(false)}
                    >
                      Contact Us
                    </NavLink>
                  </div>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <button
                ref={menuButtonRef}
                className={`lg:hidden ml-auto p-3 rounded-full text-white transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center ${
                  isOpen ? "bg-white/10" : "hover:bg-white/[0.06]"
                }`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
              >
                <span className="relative z-10">
                  {isOpen ? (
                    <FaTimes className="h-5 w-5 transform rotate-0 transition-transform duration-300" />
                  ) : (
                    <FaBars className="h-5 w-5 transform transition-transform duration-300" />
                  )}
                </span>
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        <div
          className={`lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />

        {/* Mobile Menu Sidebar */}
        <div
          id="mobile-menu"
          ref={mobileMenuRef}
          data-open={isOpen}
          className={`lg:hidden fixed top-0 right-0 h-full w-[min(100vw,20rem)] z-50 flex flex-col transform transition-transform duration-300 ease-out bg-black border-l border-white/10 ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between px-5 h-20 border-b border-white/8 shrink-0">
            <img
              src={logo}
              alt="Safe Edges Logo"
              width="32"
              height="32"
              className="h-8 w-auto"
              loading="eager"
            />
            <button
              onClick={() => setIsOpen(false)}
              className="p-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close menu"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Menu Content */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-4">
            <nav className="flex flex-col gap-0.5" aria-label="Mobile navigation">
              {[
                { to: "/", text: "Home" },
                { to: "/about-us", text: "About Us" },
                { to: "/services", text: "Services" },
                { to: "/zk-security", text: "ZK Security", badge: "ZK" },
                { to: "/reports", text: "Audits" },
                { to: "/services/ai-mcp-audit", text: "AI & MCP Audit", badge: "New" },
                { to: "https://blog.safeedges.in", text: "Blog", external: true },
                { to: "https://blog.safeedges.in/category/web3-security-case-studies-safe-edges/", text: "Case Studies", external: true },
                { to: "/contact", text: "Contact" },
              ].map((link) => {
                const badge = link.badge ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-300 bg-teal-400/10 border border-teal-400/20 px-1.5 py-0.5 rounded">
                    {link.badge}
                  </span>
                ) : null;

                if (link.external) {
                  return (
                    <a
                      key={link.to}
                      href={link.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-3 rounded-lg text-[15px] text-gray-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{link.text}</span>
                      <span className="text-gray-600 text-xs">↗</span>
                    </a>
                  );
                }

                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-3 py-3 rounded-lg text-[15px] transition-colors ${
                        isActive
                          ? "text-white bg-white/[0.06] border-l-2 border-teal-400 pl-[10px]"
                          : "text-gray-300 hover:text-white hover:bg-white/[0.04] border-l-2 border-transparent"
                      }`
                    }
                    onClick={() => setIsOpen(false)}
                  >
                    <span>{link.text}</span>
                    {badge}
                  </NavLink>
                );
              })}
            </nav>

            <div className="mt-6 pt-5 border-t border-white/8">
              <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.2em] text-gray-500">
                Products
              </p>
              <div className="flex flex-col gap-0.5">
                <a
                  href="https://open-vsx.org/extension/safeedges/bastion-security"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-3 py-3 rounded-lg text-[15px] text-gray-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <span>Bastion Security</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-300 bg-teal-400/10 border border-teal-400/20 px-1.5 py-0.5 rounded">
                    New
                  </span>
                </a>
                <a
                  href="https://crates.io/crates/swayscan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="SwayScan on crates.io"
                  className="flex items-center justify-between px-3 py-3 rounded-lg text-[15px] text-gray-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <span>SwayScan</span>
                  <span className="text-gray-600 text-xs">↗</span>
                </a>
                <a
                  href="https://evmsleuth.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="EVMSleuth website"
                  className="flex items-center justify-between px-3 py-3 rounded-lg text-[15px] text-gray-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <span>EVMSleuth</span>
                  <span className="text-gray-600 text-xs">↗</span>
                </a>
              </div>
            </div>
          </div>

          {/* Sticky CTA */}
          <div className="shrink-0 p-4 border-t border-white/8 bg-black">
            <a
              href="https://calendly.com/vivanchauhan-safeedges/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-full px-4 py-3.5 rounded-xl bg-gradient-to-r from-cyan-400 to-teal-500 text-black font-semibold text-sm hover:from-cyan-300 hover:to-teal-400 transition-all"
              onClick={() => setIsOpen(false)}
            >
              Schedule Meeting
            </a>
          </div>
        </div>

        <main className="flex-grow pt-24">
          <Outlet />
        </main>
        <Footer />
      </div>
    </ThemeProvider>
  );
};

export default MainLayout;